import React, { useEffect, useState } from 'react';
import { Form, Input, Icon, Select, Row, Col, Avatar, Card, Spin, Upload, Popover, Button, Checkbox, List } from 'antd';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'dva';
import { Prompt } from 'dva/router';
import _ from 'lodash';
import { ButtonBar } from '../../../components';
import config from '../../../config';
import styles from './../styles.less';
import PopoverText from '../../Projects/PopoverText';
import helpers from '../../../utils/helpers';
import CurrentPasswordModal from '../../CurrentPasswordModal';
import { UserTwoFACard } from '../index';
import { getAvatarUrl } from '../../../utils/avatar';

const UserItem = (props) => {
  const [submitted, setSubmitted] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [projects, setProjects] = useState(props.users.userEdit.projects);
  const [openCurrentPasswordModalData, setOpenCurrentPasswordModalData] = useState(null);
  const id = props.match.params.userId;
  const locales = config['app.locales'];
  const isAdministrationUsers = window.location.href.includes('administration/users');
  const labelPreset = isAdministrationUsers ? 'Administration.User' : 'User';
  const requiredPass = props.form.getFieldValue('password') || props.form.getFieldValue('password_repeat');
  const avatarData = id ? (avatar && avatar.remove ? null : (props.users.userEdit.avatar ? `${getAvatarUrl(isAdministrationUsers ? null : props.app.defaultUiSettings.core_client_id, props.users.userEdit.avatar)}` : null)) : null;

  useEffect(() => {
    props.dispatch({
      type: 'header/update',
      payload: {
        title: id
          ? `Component.${labelPreset}.Administration.Edit.Form.Title`
          : `Component.${labelPreset}.Administration.New.Form.Title`,
      },
    });
    if (id) {
      props.dispatch({
        type: 'users/getUserInfo',
        payload: { id },
      });
    }
  }, []);

  useEffect(() => {
    if (!_.isEqual(props.users.userEdit.projects, projects)) {
      setProjects(props.users.userEdit.projects);
    }
  }, [props.users.userEdit.projects]);

  const showNotification = () => {
    props.dispatch({
      type: 'app/addNotification',
      payload: {
        type: 'success',
        message: 'Common.Success',
        description: `Component.${labelPreset}.Administration.Edit.Success.Text`,
      },
    }).then(
      props.dispatch({ type: 'app/showNotification' }),
    );
  };

  const cancelAction = () => {
    props.dispatch({
      type: 'app/goToRoute',
      payload: { routeName: isAdministrationUsers ? 'ADMINISTRATION_USER_LIST' : 'USERS_LIST' },
    });
  };

  const getRemainingProjects = (index) => {
    return props.app.projects.filter((project) => {
      return !_.without(projects, projects[index]).some(item => item.id === project.id);
    });
  };

  const addProject = () => {
    setProjects([...(projects || []), { id: null, role_id: 1 }]);
  };

  const setProjectData = (index, projectId) => {
    setProjects(projects.map((item, i) => (index === i ? { ...props.app.projects.find(project => project.id === projectId), role_id: 1 } : item)));
  };

  const removeProject = (index) => {
    setProjects(projects.filter((project, i) => index !== i));
  };

  const createUser = (data) => {
    props.dispatch({
      type: 'users/create',
      payload: {
        ...data,
      },
    }).then((userId) => {
      if (avatar) {
        props.dispatch({
          type: 'users/updateAvatar',
          payload: {
            id: userId,
            avatar: avatar.file,
          },
        });
      }
    });
  };

  const handleResponse = (res) => {
    return res.success ? res : Promise.reject();
  };

  const updateUser = async (data) => {
    if (data.password && data.password_repeat) {
      const passwordRequest = await props.dispatch({
        type: 'users/updateUserPassword',
        payload: {
          id,
          password: data.password,
          password_confirmation: data.password_repeat,
          current_password: data.current_password,
        },
      });
      if (!passwordRequest.success) {
        return;
      }
    }

    const updateRequest = props.dispatch({
      type: 'users/update',
      payload: { ...data, id },
    }).then(res => handleResponse(res));

    const assignRequest = props.dispatch({
      type: 'users/assign',
      payload: {
        id,
        ...data,
      },
    }).then(res => handleResponse(res));

    let avatarRequest;
    if (avatar) {
      const payload = { id };
      if (avatar.file) {
        payload.avatar = avatar.file;
      }
      if (avatar.remove) {
        payload.remove = avatar.remove;
      }
      avatarRequest = props.dispatch({
        type: 'users/updateAvatar',
        payload,
      }).then(res => handleResponse(res));
    }

    Promise.all([updateRequest, assignRequest, avatarRequest].filter(Boolean)).then(() => {
      showNotification();
      props.dispatch({
        type: 'app/goToRoute',
        payload: { routeName: isAdministrationUsers ? 'ADMINISTRATION_USER_LIST' : 'USERS_LIST' },
      });
    });
  };

  const sendFormRequest = (data, currentPassword) => {
    setSubmitted(true);

    const { assign } = data;

    if (currentPassword) {
      // eslint-disable-next-line no-param-reassign
      data.current_password = currentPassword;
    }

    if (assign) {
      // eslint-disable-next-line no-param-reassign
      data.assign = assign.filter(Boolean);
    }

    if (id) {
      updateUser(data);
    } else {
      const sendData = { ...data };
      if (isAdministrationUsers) {
        sendData.client_id = null;
        sendData.super_admin = true;
      } else {
        sendData.client_id = props.app.selectedClient.id;
      }
      createUser(sendData);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    props.form.validateFieldsAndScroll((error, values) => {
      if (!error) {
        if (props.form.getFieldValue('password')) {
          setOpenCurrentPasswordModalData(values);
          return;
        }

        sendFormRequest(values);
      }
    });
  };

  const comparePasswords = (rule, value, callback) => {
    const fieldName = rule.field === 'password_repeat' ? 'password' : 'password_repeat';
    const form = props.form;
    const password = form.getFieldValue(fieldName);

    if (value && value !== password) {
      callback(props.intl.formatMessage({ id: 'Common.ConfirmationPasswordError' }));
    } else {
      callback();
    }
  };

  const onPasswordChange = (rule, value, callback) => {
    if (props.form.getFieldValue('password_repeat')) {
      comparePasswords(rule, value, callback);
    }

    if (value && !helpers.isValidPassword(value)) {
      callback(props.intl.formatMessage({ id: 'Common.ConfirmationPasswordSecureRule' }));
    } else {
      callback();
    }
  };

  const onBeforeFileUpload = (file) => {
    const reader = new window.FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('loadend', () => setAvatar({
      shouldBeRemoved: false,
      url: reader.result,
      file,
    }));
    return false;
  };

  const removeAvatar = () => setAvatar({ remove: true });

  return (
    <Spin spinning={!!props.loading.models.users}>
      <Prompt
        when={!submitted && (props.form.isFieldsTouched() || !!avatar)}
        message={props.intl.locale}
      />
      <div className={styles.containerLarge}>
        <Form onSubmit={handleSubmit} className={styles.editUser}>
          <Card className={styles.noMarginTop}>
            <div className={styles.inputGroup}>
              <Row type="flex" gutter={32}>
                <Col xs={24} sm={12}>
                  <h3><FormattedMessage id={`Component.${labelPreset}.Administration.Users.Edit.Title.UserName`} /></h3>
                  <FormattedMessage id={`Component.${labelPreset}.Administration.Users.${id ? 'Edit' : 'New'}.Description`} />
                </Col>
                <Col xs={24} sm={12}>
                  <div className={styles.avatarWr}>
                    {((avatar && avatar.url) || avatarData) && <Icon onClick={removeAvatar} type="close-circle" /> }
                    <Upload
                      name="avatar"
                      accept=".jpg,.jpeg,.png,.gif"
                      listType="picture"
                      showUploadList={false}
                      beforeUpload={onBeforeFileUpload}
                    >
                      <Spin spinning={!!props.loading.effects['users/updateAvatar']}>
                        {
                          (avatar && avatar.url) || avatarData ?
                            <Avatar src={(avatar && avatar.url) || avatarData} className={styles.widthResize} size="large" /> :
                            <Avatar size="large" icon="user" className={styles.widthResize} />
                        }
                      </Spin>
                    </Upload>
                  </div>
                </Col>
              </Row>
            </div>
            <div className={styles.inputGroup}>
              <h3><FormattedMessage id="Component.User.Administration.Users.New.PersonalData" /></h3>
              <Row type="flex" gutter={32}>
                <Col xs={24} sm={12}>
                  <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.Edit.Form.fName" />}>
                    {props.form.getFieldDecorator('first_name', {
                      initialValue: id ? props.users.userEdit.first_name : '',
                      rules: [{
                        required: true,
                        min: 1,
                        max: 255,
                        message: props.intl.formatMessage({ id: 'Component.User.Administration.Users.Edit.Form.Error' }),
                      }],
                    })(
                      <Input maxLength={255} />,
                    )}
                  </Form.Item>
                  <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.Edit.Form.lName" />}>
                    {props.form.getFieldDecorator('last_name', {
                      initialValue: id ? props.users.userEdit.last_name : '',
                      rules: [{
                        min: 1,
                        max: 255,
                        required: true,
                        message: props.intl.formatMessage({ id: 'Component.User.Administration.Users.Edit.Form.Error' }),
                      }],
                    })(
                      <Input maxLength={255} />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row type="flex" gutter={32}>
                <Col xs={24} sm={12}>
                  <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.Edit.Form.Email" />}>
                    {props.form.getFieldDecorator('email', {
                      initialValue: id ? props.users.userEdit.email : '',
                      rules: [{
                        min: 3,
                        max: 255,
                        required: true,
                        type: 'email',
                        message: props.intl.formatMessage({ id: 'Component.User.Administration.Users.Edit.Form.Error' }),
                      }],
                    })(
                      <Input maxLength={255} />,
                    )}
                  </Form.Item>
                </Col>
                {id && (
                  <React.Fragment>
                    <Col xs={24} sm={12}>
                      <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.Edit.Form.Email2" />}>
                        {props.form.getFieldDecorator('email-repeat', {
                          initialValue: id ? props.users.userEdit.email : '',
                          rules: [{
                            required: true,
                            min: 3,
                            max: 255,
                            type: 'email',
                            message: props.intl.formatMessage({ id: 'Component.User.Administration.Users.Edit.Form.Error' }),
                          }],
                        })(
                          <Input maxLength={255} />,
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.Edit.Form.Password" />}>
                        {props.form.getFieldDecorator('password', {
                          rules: [{
                            required: requiredPass,
                            message: props.intl.formatMessage({ id: 'Component.User.Administration.Users.Edit.Form.Error' }),
                          }, {
                            validator: onPasswordChange.bind(this),
                          }],
                          validateTrigger: ['onChange', 'onKeyUp'],
                        })(
                          <Input maxLength={255} type="password" autoComplete="new-password" />,
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.Edit.Form.Password2" />}>
                        {props.form.getFieldDecorator('password_repeat', {
                          initialValue: '',
                          rules: [{
                            required: requiredPass,
                            message: props.intl.formatMessage({ id: 'Component.User.Administration.Users.Edit.Form.Error' }),
                          }, {
                            validator: comparePasswords.bind(this),
                          }],
                          validateTrigger: 'onKeyUp',
                        })(
                          <Input maxLength={255} type="password" autoComplete="new-password" />,
                        )}
                      </Form.Item>
                    </Col>
                  </React.Fragment>
                )}
                <Col span={24}>
                  <Form.Item className={!id ? styles.localeSelect : ''} label={<FormattedMessage id="Component.User.Administration.Users.Edit.Form.Lang" />}>
                    {props.form.getFieldDecorator('locale', {
                      initialValue: id ? props.users.userEdit.locale : '',
                      rules: [{
                        required: true,
                        message: props.intl.formatMessage({ id: 'Component.User.Administration.Users.Edit.Form.Error' }),
                      }],
                    })(
                      <Select style={{ width: '100%' }}>
                        {locales.map(item => (
                          <Select.Option value={item} key={item}>
                            <FormattedMessage id={`Common.Languages.Name.${item}`} />
                          </Select.Option>
                        ))}
                      </Select>,
                    )}
                  </Form.Item>
                  {props.app.user.super_admin && !isAdministrationUsers ?
                    <Form.Item>
                      {props.form.getFieldDecorator('super_admin', {
                        valuePropName: 'checked',
                        initialValue: props.users.userEdit.super_admin,
                      })(
                        <Checkbox>
                          <FormattedMessage id="Component.User.Administration.Users.New.SuperAdmin" />
                        </Checkbox>,
                      )}
                      <Popover
                        content={
                          <List>
                            <List.Item>
                              <FormattedMessage id={'Component.User.Administration.Users.New.SuperAdmin.Description'} />
                            </List.Item>
                          </List>
                        }
                      >
                        <a href="##" className={styles.btnNoBorder}>
                          <Icon type="question-circle-o" />
                        </a>
                      </Popover>
                    </Form.Item>
                    : ''
                  }
                </Col>
              </Row>
            </div>
          </Card>
          {!isAdministrationUsers && (
            <Card className={styles.noMarginTop}>
              <h3><FormattedMessage id="Component.User.Administration.Users.Edit.AccessTitle" /></h3>
              <Row type="flex" gutter={32}>
                {(projects || []).map((project, index) => {
                  return (
                    <Col key={index} md={24} lg={12}>
                      <Row type="flex" gutter={32}>
                        <Col span={17}>
                          <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.New.Form.Account" />}>
                            {props.form.getFieldDecorator(`assign.${project.id}.account`, {
                              initialValue: project.id || '',
                              required: true,
                            })(
                              <Select style={{ width: '100%' }} onSelect={setProjectData.bind(this, index)}>
                                {getRemainingProjects(index).map(item => (
                                  <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>
                                ))}
                              </Select>,
                            )}
                          </Form.Item>
                          <Form.Item label={<FormattedMessage id="Component.User.Administration.Users.New.Form.Role" />}>
                            {props.form.getFieldDecorator(`assign.${project.id}.role`, {
                              initialValue: project.role_id,
                              required: true,
                            })(
                              <Select style={{ width: '100%' }}>
                                {props.app.roles.map(
                                  role => <Select.Option key={role.id} value={role.id}>{role.label}</Select.Option>,
                                )}
                              </Select>,
                            )}
                          </Form.Item>
                        </Col>
                        <Col>
                          <div className={[styles.btnNoBorder, styles.removeAccount].join(' ')}>
                            <Icon type="close" onClick={() => removeProject(index)} />
                          </div>
                          <Popover
                            content={PopoverText()}
                            trigger="click"
                            title={props.intl.formatMessage({ id: 'Component.AccountSettings.New.AvailableRoles.Title' })}
                          >
                            <div className={[styles.btnNoBorder, styles.infoLabel].join(' ')}>
                              <Icon type="question-circle-o" />
                            </div>
                          </Popover>
                        </Col>
                      </Row>
                    </Col>
                  );
                })}
              </Row>
              {(projects ? projects.length : 0) < props.app.projects.length && (
                <Row type="flex" gutter={32}>
                  <Col span={7}>
                    <Button
                      size="large"
                      type="primary"
                      onClick={() => addProject()}
                      className={styles.btnWithBlueBorders}
                    >
                      <Icon type="plus" />
                      <FormattedMessage id="Component.User.Administration.Users.Edit.AddAcount" />
                    </Button>
                  </Col>
                </Row>
              )}
            </Card>
          )}
          {props.users.userEdit.id && (
            <UserTwoFACard
              user={props.users.userEdit}
            />
          )}
          <ButtonBar cancelAction={cancelAction} />
        </Form>
      </div>
      {!!openCurrentPasswordModalData && (
        <CurrentPasswordModal
          visible={!!openCurrentPasswordModalData}
          onCancel={() => setOpenCurrentPasswordModalData(null)}
          onSuccess={currentPassword => sendFormRequest(openCurrentPasswordModalData, currentPassword)}
        />)}
    </Spin>
  );
};

export default connect(
  ({ app, users, loading }) => ({ app, users, loading }))(Form.create()(injectIntl(UserItem)));



// WEBPACK FOOTER //
// ./src/components/UsersAdministration/UserItem/UserItem.js